import React, { useEffect, useState } from 'react';
import { supabase } from '../../services/supabase';
import '../../styles/UserRoleManager.css';

const UserRoleManager = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error: dbError } = await supabase
        .from('admin_users')
        .select('id, email, created_at');

      if (dbError) throw dbError;
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data, error: dbError } = await supabase
        .from('admin_roles')
        .select('id, name, description');

      if (dbError) throw dbError;
      setRoles(data || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const selectUser = async (userId) => {
    setSelectedUser(userId);
    try {
      const { data, error: dbError } = await supabase
        .from('admin_user_roles')
        .select('role_id')
        .eq('user_id', userId);

      if (dbError) throw dbError;
      setSelectedRoles((data || []).map(r => r.role_id));
    } catch (err) {
      console.error('Error fetching user roles:', err);
      setError(err.message);
    }
  };

  const toggleRole = async (roleId) => {
    if (!selectedUser) return;

    try {
      if (selectedRoles.includes(roleId)) {
        const { error: dbError } = await supabase
          .from('admin_user_roles')
          .delete()
          .eq('user_id', selectedUser)
          .eq('role_id', roleId);

        if (dbError) throw dbError;
        setSelectedRoles(selectedRoles.filter(r => r !== roleId));
      } else {
        const { error: dbError } = await supabase
          .from('admin_user_roles')
          .insert({ user_id: selectedUser, role_id: roleId });

        if (dbError) throw dbError;
        setSelectedRoles([...selectedRoles, roleId]);
      }
      setError(null);
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(err.message);
    }
  };

  const getUserRoleNames = (userId) => {
    const userRoles = users.find(u => u.id === userId)?.roles || [];
    return userRoles.map(r => r.name).join(', ') || 'No roles';
  };

  return (
    <div className="user-role-manager">
      <h2>User Role Management</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="role-management-container">
        <div className="users-list">
          <h3>Users</h3>
          {loading ? (
            <p>Loading users...</p>
          ) : users.length === 0 ? (
            <p>No users found</p>
          ) : (
            <ul>
              {users.map(user => (
                <li
                  key={user.id}
                  className={selectedUser === user.id ? 'selected' : ''}
                  onClick={() => selectUser(user.id)}
                >
                  <div className="user-info">
                    <span className="email">{user.email}</span>
                    <span className="created">Joined: {new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="roles-assignment">
          <h3>Assign Roles</h3>
          {selectedUser ? (
            <div className="roles-grid">
              {roles.map(role => (
                <div key={role.id} className="role-checkbox">
                  <input
                    type="checkbox"
                    id={`role-${role.id}`}
                    checked={selectedRoles.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                  />
                  <label htmlFor={`role-${role.id}`}>
                    <span className="role-name">{role.name}</span>
                    <span className="role-desc">{role.description}</span>
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <p>Select a user to assign roles</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRoleManager;
